import React, { createContext, useContext, useState, useEffect } from 'react';
import createApiInstance from "../utilities/apiAuth";
import isTokenExpired from '../utilities/checkTokenExpiration';
import { useNavigate } from "react-router-dom";

const SelectedFranchiseeContext = createContext();

export function SelectedFranchiseeProvider({ children }) {
  const navigate = useNavigate();
  const [selectedFranchisee, setSelectedFranchisee] = useState(null);
  const [franchisees, setFranchisees] = useState([]);

  
  const fetchFranchisees = async () => {
    
    if (isTokenExpired()) {
      console.log('Token expired - skipping franchisee fetch');
      return;
    }
    
    const api = createApiInstance(navigate);
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        const res = await api.get('/franchisees/list');
        setFranchisees(res.data);
      } else {
        console.log('No User Logged In');
      }
    } catch (error) {
      // Don't log token expiration errors to avoid console spam
      if (error.message !== 'Token expired') {
        console.log(error);
      }
    }
  };

  useEffect(() => {
    fetchFranchisees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Load selected franchisee from localStorage on mount
  useEffect(() => {
    const savedFranchisee = localStorage.getItem('selectedFranchisee');
    if (savedFranchisee) {
      try {
        setSelectedFranchisee(JSON.parse(savedFranchisee));
      } catch (error) {
        console.error('Error parsing saved franchisee:', error);
        localStorage.removeItem('selectedFranchisee');
      }
    }
  }, []);

  // Save to localStorage whenever selectedFranchisee changes
  useEffect(() => {
    if (selectedFranchisee) {
      localStorage.setItem('selectedFranchisee', JSON.stringify(selectedFranchisee));
    } else {
      localStorage.removeItem('selectedFranchisee');
    }
  }, [selectedFranchisee]);


  const updateSelectedFranchisee = (franchisee) => {
    setSelectedFranchisee(franchisee);
  };

  const clearSelectedFranchisee = () => {
    setSelectedFranchisee(null);
    localStorage.removeItem('selectedFranchisee');
  };

  const getHubspotApiKey = () => {
    return selectedFranchisee?.client_type?.hubspot_api_key || null;
  };

  const getHubspotId = () => {
    return selectedFranchisee?.UserFranchiseeAssociation?.hubspotID || null;
  };

  const getExternalEmail = () => {
    return selectedFranchisee?.UserFranchiseeAssociation?.external_email || null;
  };

  const getFranchiseeName = () => {
    return selectedFranchisee?.name || null;
  };

  const getFranchiseeId = () => {
    return selectedFranchisee?.id || null;
  };

  return (
    <SelectedFranchiseeContext.Provider
      value={{
        selectedFranchisee,
        updateSelectedFranchisee,
        clearSelectedFranchisee,
        getHubspotApiKey,
        getHubspotId,
        getExternalEmail,
        getFranchiseeName,
        getFranchiseeId, 
        franchisees
      }}
    >
      {children}
    </SelectedFranchiseeContext.Provider>
  );
}

export function useSelectedFranchisee() {
  const context = useContext(SelectedFranchiseeContext);
  if (context === undefined) {
    throw new Error('useSelectedFranchisee must be used within a SelectedFranchiseeProvider');
  }
  return context;
}
